/**
 * Spending the threat budget (03 §5.1 step 6; 02 §12): 40 points at TR 1 to 200 at TR 9,
 * biased north - drop band 10 %, approaches 35 %, belt 30 %, goal 25 % - on the cost table.
 * The garrison is bought first in every pool, fieldworks afterwards, so mines and wire are
 * laid around the patrols rather than under them. What a pool cannot spend moves north.
 */

import type { Pcg32 } from '../sim/rng';
import { buyAlarmPost, buyBarracks, buyMinefield, buyProximityPost, buySearchlight, buyWireRun } from './fieldworks';
import { buyEmplacement, buyMiniBunker, buyOutpost, buyPatrol, buySentry } from './garrison';
import { addPatrol } from './patrols';
import { BUDGET, COST, LIMITS } from './tuning';
import type { BandKind, GenCtx } from './types';

interface Purchase {
  id: string;
  /** The least it can cost: filters what a pool can still afford. */
  cost: number;
  phase: 'garrison' | 'fieldworks';
  weight: Record<BandKind, number>;
  minThreat?: number;
  allowed?: (ctx: GenCtx, band: BandKind) => boolean;
  /** Returns the points spent; 0 when it could not be placed. */
  buy: (ctx: GenCtx, rng: Pcg32, band: number, points: number) => number;
}

const inDrop = (ctx: GenCtx): number => ctx.map.spawns.filter((s) => s.z > (ctx.bands[0]?.z0 ?? Infinity)).length;
const count = (ctx: GenCtx, kind: string): number => ctx.map.spawns.filter((s) => s.kind === kind).length;
/** Lamps are lit at night and at dusk (hazards/searchlights.ts); by daylight one is wasted points. */
const dark = (ctx: GenCtx): boolean => ctx.params.timeOfDay === 'night' || ctx.params.timeOfDay === 'dusk';
const minedDrop = (ctx: GenCtx): boolean => ctx.map.mineFields.some((f) => f.rect.z0 > (ctx.bands[0]?.z0 ?? 0));

const PURCHASES: readonly Purchase[] = [
  {
    id: 'sentry', cost: COST.sentry, phase: 'garrison', weight: { drop: 2, approach: 4, belt: 3, goal: 6 },
    allowed: (ctx, band) => band !== 'drop' || inDrop(ctx) < 2,
    buy: (ctx, rng, band, points) => buySentry(ctx, rng, band, points),
  },
  {
    id: 'patrol2', cost: COST.patrol2, phase: 'garrison', weight: { drop: 3, approach: 5, belt: 3, goal: 3 },
    allowed: (ctx, band) => band !== 'drop' || inDrop(ctx) === 0,
    buy: (ctx, rng, band, points) => buyPatrol(ctx, rng, band, points, 2),
  },
  {
    id: 'patrol3', cost: COST.patrol3, phase: 'garrison', minThreat: 3,
    weight: { drop: 0, approach: 2.5, belt: 1.5, goal: 2 },
    buy: (ctx, rng, band, points) => buyPatrol(ctx, rng, band, points, 3),
  },
  {
    id: 'outpost', cost: COST.outpost, phase: 'garrison', weight: { drop: 0, approach: 2, belt: 0, goal: 0 },
    buy: buyOutpost,
  },
  {
    id: 'emplacement', cost: COST.mgNest, phase: 'garrison', weight: { drop: 0, approach: 2, belt: 7, goal: 3 },
    buy: buyEmplacement,
  },
  {
    id: 'flamethrower', cost: COST.flamethrower, phase: 'garrison', minThreat: LIMITS.specialsFrom,
    weight: { drop: 0, approach: 1, belt: 1.5, goal: 1.5 },
    allowed: (ctx) => count(ctx, 'flamethrower') < 1 + Math.floor((ctx.params.threat - 5) / 2),
    buy: (ctx, rng, band, points) => buySentry(ctx, rng, band, points, 'flamethrower'),
  },
  {
    id: 'rocketTrooper', cost: COST.rocketTrooper, phase: 'garrison', minThreat: LIMITS.specialsFrom,
    weight: { drop: 0, approach: 0.8, belt: 1.5, goal: 1.2 },
    // Mission 10's rocket troopers are its executioners (objectives/executioners.ts knows them by kind): none besides.
    allowed: (ctx) =>
      ctx.spec.id !== 'hostages' && count(ctx, 'rocketTrooper') < 1 + Math.floor((ctx.params.threat - 5) / 2),
    buy: (ctx, rng, band, points) => buySentry(ctx, rng, band, points, 'rocketTrooper'),
  },
  {
    id: 'miniBunker', cost: COST.miniBunker, phase: 'garrison', weight: { drop: 0, approach: 0.8, belt: 1.5, goal: 0 },
    minThreat: LIMITS.miniBunkerFrom,
    allowed: (ctx) => ctx.spec.id !== 'sam-site' && count(ctx, 'miniBunker') < 2,
    buy: buyMiniBunker,
  },
  {
    id: 'minefield', cost: COST.minefield, phase: 'fieldworks', weight: { drop: 2, approach: 4, belt: 6, goal: 1 },
    // The drop band gets one minefield at most, and only from threat 4 (03 §5.1 step 8).
    allowed: (ctx, band) => band !== 'drop' || (ctx.params.threat >= LIMITS.minefieldInDropFrom && !minedDrop(ctx)),
    buy: buyMinefield,
  },
  {
    id: 'wireRun', cost: COST.wireRun, phase: 'fieldworks', weight: { drop: 1, approach: 2, belt: 2, goal: 0.5 },
    buy: buyWireRun,
  },
  {
    id: 'proximityPost', cost: COST.proximityPost, phase: 'fieldworks',
    weight: { drop: 0, approach: 1, belt: 3, goal: 1 },
    allowed: (ctx) => ctx.kit.proximityPosts || ctx.spec.id === 'diversion',
    buy: buyProximityPost,
  },
  {
    id: 'searchlight', cost: COST.searchlight, phase: 'fieldworks', weight: { drop: 0, approach: 1, belt: 3, goal: 2 },
    allowed: dark,
    buy: buySearchlight,
  },
  {
    id: 'alarmPost', cost: COST.alarmPost, phase: 'fieldworks', weight: { drop: 0, approach: 1, belt: 1.5, goal: 1.5 },
    buy: buyAlarmPost,
  },
  {
    id: 'barracks', cost: COST.barracks, phase: 'fieldworks', minThreat: 3,
    weight: { drop: 0, approach: 0, belt: 0.5, goal: 2 },
    buy: (ctx, rng, _band, points) => buyBarracks(ctx, rng, points),
  },
];

/** Buy from one pool until nothing affordable fits; returns the points left over. */
function spend(ctx: GenCtx, rng: Pcg32, kind: BandKind, phase: Purchase['phase'], points: number): number {
  const bandIndexes = ctx.bands.map((b, i) => (b.kind === kind ? i : -1)).filter((i) => i >= 0);
  let left = points;
  let misses = 0;
  while (left >= COST.sentry && misses < 10) {
    const options = PURCHASES.filter((p) =>
      p.phase === phase && p.weight[kind] > 0 && p.cost <= left
      && ctx.params.threat >= (p.minThreat ?? 1) && (p.allowed?.(ctx, kind) ?? true));
    if (options.length === 0) break;
    let total = 0;
    for (const p of options) total += p.weight[kind];
    let roll = rng.float() * total;
    let choice = options[0];
    for (const p of options) {
      roll -= p.weight[kind];
      if (roll < 0) {
        choice = p;
        break;
      }
    }
    const band = rng.pick(bandIndexes);
    const spent = choice ? choice.buy(ctx, rng, band, left) : 0;
    if (spent > 0) left -= spent;
    else misses++;
  }
  return left;
}

export function threatBudget(threat: number): number {
  const t = Math.min(1, Math.max(0, (threat - 1) / 8));
  return BUDGET.at1 + (BUDGET.at9 - BUDGET.at1) * t;
}

/**
 * Spend the whole budget, south to north. Returns the points that found no use. `prepaid` is what was
 * bought for the approach bands before the budget was opened (ROUND8: the dog teams, `dogTeams.ts`); a
 * debt the approaches cannot cover is carried north like any balance.
 */
export function spendBudget(ctx: GenCtx, prepaid = 0): number {
  const rng = ctx.rng.fork('budget');
  const total = threatBudget(ctx.params.threat) * ctx.budgetScale;
  const share = ctx.params.modifiers.includes('dense-minefields') ? BUDGET.fieldworksDense : BUDGET.fieldworks;
  const pools: BandKind[] = ['drop', 'approach', 'belt', 'goal'];
  const works: number[] = [];
  let carry = 0;
  for (const kind of pools) {
    const pool = total * BUDGET.split[kind];
    works.push(pool * share);
    const paid = kind === 'approach' ? prepaid : 0;
    carry = spend(ctx, rng, kind, 'garrison', pool * (1 - share) - paid + carry);
  }
  pools.forEach((kind, i) => {
    carry = spend(ctx, rng, kind, 'fieldworks', (works[i] ?? 0) + carry);
  });
  // Whatever is left buys men for the goal zone, which is never short of posts.
  return spend(ctx, rng, 'goal', 'garrison', carry);
}

/** `high-alert`: patrols are doubled up (02 §6.3) - a second group walks each kind of beat. */
export function doublePatrols(ctx: GenCtx): void {
  const rng = ctx.rng.fork('doubled-patrols');
  for (const group of [...ctx.patrolGroups]) {
    if (ctx.map.spawns.length + 2 > ctx.spawnCap) break;
    // Never a second group in the drop band: it holds one pair at most.
    if (ctx.bands[group.band]?.kind === 'drop') continue;
    addPatrol(ctx, rng, group.band, [group.kinds[0] ?? 'rifleman', 'rifleman'], 3);
  }
}
